'use client';

import Link from 'next/link';
import { ArrowLeft, MapPin, Move, Layers, Settings } from 'lucide-react';
import StandardLayout from '../../components/layout/StandardLayout';
import '../../components/editor/Documentation.scss';
import CodeExample from '../../components/ui/CodeExample';

const basicWorkflowCode = `import fullcontrol as fc

# Initialize
steps = []

# Add some geometry
steps.extend(fc.rectangleXY(
  start_point=fc.Point(x=0, y=0, z=0.2),
  x_size=20,
  y_size=20
))

# Transform to G-code
gcode = fc.transform(steps, 'gcode')`;

const simpleSquareCode = `import fullcontrol as fc

# Parameters
layer_height = 0.2
size = 20

# Initialize
steps = []

# Move to start position (travel move)
steps.append(fc.Point(x=0, y=0, z=layer_height))

# Turn on extrusion
steps.append(fc.Extruder(on=True))

# Create square
steps.extend(fc.rectangleXY(
  start_point=fc.Point(x=0, y=0, z=layer_height),
  x_size=size,
  y_size=size
))

# Turn off extrusion
steps.append(fc.Extruder(on=False))

# Move up (travel move)
steps.append(fc.Point(x=0, y=0, z=layer_height * 2))

# Generate G-code
gcode = fc.transform(steps, 'gcode')`;

export default function BasicsPage() {
  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          {/* Navigation */}
          <nav className="docs-nav">
            <Link href="/docs" className="back-link">
              <ArrowLeft size={16} />
              Back to Documentation
            </Link>
          </nav>

          {/* Header */}
          <header className="docs-header">
            <h1>FullControl Basics</h1>
            <p>Learn the fundamental concepts of FullControl</p>
          </header>

          {/* Content */}
          <div className="docs-content">
            <section className="docs-section">
              <h2>What is FullControl?</h2>
              <p>
                FullControl is a Python library that gives you complete control over 3D printing toolpaths.
                Instead of working with traditional CAD models and slicers, you define exactly where the
                printer moves and what it does at each point.
              </p>
            </section>

            <section className="docs-section">
              <h2>Core Concepts</h2>

              <div className="concept-grid">
                <div className="concept-card">
                  <MapPin className="concept-icon" />
                  <h3>Points</h3>
                  <p>The foundation of all designs. Every position in 3D space is defined as a Point with x, y, and z coordinates.</p>
                  <CodeExample language="python" title="Point Definition" code={`point = fc.Point(x=10, y=20, z=0.2)`} />
                </div>

                <div className="concept-card">
                  <Move className="concept-icon" />
                  <h3>Steps</h3>
                  <p>Individual instructions that tell the printer what to do. Steps can be movements, extrusion changes, or other commands.</p>
                  <CodeExample
                    language="python"
                    title="Steps List"
                    code={`steps = [
    fc.Point(x=0, y=0, z=0.2),
    fc.Point(x=10, y=0, z=0.2),
    fc.Extruder(on=False)
]`}
                  />
                </div>

                <div className="concept-card">
                  <Layers className="concept-icon" />
                  <h3>Layer Management</h3>
                  <p>Build up your design layer by layer, with full control over layer height and transitions.</p>
                  <CodeExample
                    language="python"
                    title="Layer Loop"
                    code={`layer_height = 0.2
for layer in range(5):
    z = layer * layer_height
    # Add layer geometry here`}
                  />
                </div>

                <div className="concept-card">
                  <Settings className="concept-icon" />
                  <h3>State Objects</h3>
                  <p>Control printer settings like temperature, speed, and extrusion throughout your design.</p>
                  <CodeExample
                    language="python"
                    title="State Objects"
                    code={`steps.append(fc.Extruder(on=True))
steps.append(fc.ExtrusionRate(rate=0.5))
steps.append(fc.PrintSpeed(speed=20))`}
                  />
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>Basic Workflow</h2>
              <p>Every FullControl design follows the same basic pattern:</p>
              <ol>
                <li><strong>Import the library:</strong> <span className="inline-code">import fullcontrol as fc</span></li>
                <li><strong>Create a list:</strong> Start with an empty list to store your steps</li>
                <li><strong>Add geometry:</strong> Use FullControl functions to create shapes and movements</li>
                <li><strong>Add controls:</strong> Insert printer commands like extrusion and speed changes</li>
                <li><strong>Transform to G-code:</strong> Convert your steps to printer instructions</li>
              </ol>
              <CodeExample language="python" title="Basic Workflow" code={basicWorkflowCode} />
            </section>

            <section className="docs-section">
              <h2>Common Functions</h2>

              <div className="function-category">
                <h3>Geometry Functions</h3>
                <ul>
                  <li><span className="inline-code">fc.rectangleXY()</span> - Create rectangular shapes</li>
                  <li><span className="inline-code">fc.circleXY()</span> - Create circular paths</li>
                  <li><span className="inline-code">fc.lineXY()</span> - Create straight lines</li>
                  <li><span className="inline-code">fc.arcXY()</span> - Create curved paths</li>
                </ul>
              </div>

              <div className="function-category">
                <h3>Movement Functions</h3>
                <ul>
                  <li><span className="inline-code">fc.travel_to()</span> - Move without extruding</li>
                  <li><span className="inline-code">fc.move_polar()</span> - Move in polar coordinates</li>
                  <li><span className="inline-code">fc.relative_move()</span> - Move relative to current position</li>
                </ul>
              </div>

              <div className="function-category">
                <h3>Control Functions</h3>
                <ul>
                  <li><span className="inline-code">fc.Extruder(on=True/False)</span> - Turn extrusion on/off</li>
                  <li><span className="inline-code">fc.PrintSpeed(speed=value)</span> - Set printing speed</li>
                  <li><span className="inline-code">fc.ExtrusionRate(rate=value)</span> - Control extrusion amount</li>
                  <li><span className="inline-code">fc.Hotend(temp=value)</span> - Set nozzle temperature</li>
                </ul>
              </div>
            </section>

            <section className="docs-section">
              <h2>Working with Coordinates</h2>
              <p>
                FullControl uses a standard 3D coordinate system:
              </p>
              <ul>
                <li><strong>X-axis:</strong> Left/right movement (usually bed width)</li>
                <li><strong>Y-axis:</strong> Forward/backward movement (usually bed depth)</li>
                <li><strong>Z-axis:</strong> Up/down movement (layer height)</li>
              </ul>
              <CodeExample language="python" title="Simple Square" code={simpleSquareCode} />
            </section>

            <section className="docs-section">
              <h2>Next Steps</h2>
              <p>
                Now that you understand the basics, you can explore more advanced topics:
              </p>
              <div className="next-steps">
                <Link href="/docs/geometry" className="next-step-card">
                  <Move className="step-icon" />
                  <div>
                    <h4>Geometry Functions</h4>
                    <p>Learn about all the built-in shape and movement functions</p>
                  </div>
                </Link>
                <Link href="/docs/examples" className="next-step-card">
                  <Settings className="step-icon" />
                  <div>
                    <h4>Examples Gallery</h4>
                    <p>See practical examples of FullControl in action</p>
                  </div>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </StandardLayout>
  );
}
